import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  getMessageConversations, getClientMessages, markClientMessagesRead, postProjectComment,
  getMentionableUsers, markClientHandled, uploadFile, API_BASE, getUnifiedInbox,
} from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { renderRichBody as renderMentionedBody } from "../lib/richText";
import { RichTextarea } from "../components/RichTextarea";
import {
  Loader2, Send, Search as SearchIcon, MessageSquare,
  Globe, Megaphone, Target, ExternalLink, Paperclip, FileText, X as XIcon,
  CheckCircle2, Check, Lock,
  CornerUpLeft,
} from "lucide-react";

const PRODUCT_LABEL = {
  marketing: "Marketing",
  web_design: "Web Design",
  seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID",
};
const PRODUCT_COLOR = {
  marketing: "bg-rose-100 text-rose-800 border-rose-200",
  web_design: "bg-blue-100 text-blue-800 border-blue-200",
  seo_aeo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  prospect_tools: "bg-purple-100 text-purple-800 border-purple-200",
};
const PRODUCT_ICON = { marketing: Megaphone, web_design: Globe, seo_aeo: SearchIcon, prospect_tools: Target };

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const INTERNAL_ROLES = new Set(["super_admin", "manager", "ops_coordinator"]);

function getInitials(name, override) {
  if (override) return override;
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

// Render @mentions as bold tokens inline
// Regex: matches http(s):// links up to the next whitespace. Includes the
// full path/query so Drive/Docs/Loom URLs stay clickable in one piece.
// Trailing sentence punctuation is peeled off after the match.

export default function MessagesPage() {
  const { user } = useAuth();
  const { clientId: routeClientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const staffFilter = searchParams.get("staff") || "";
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  // When drilled in from Unified Inbox with a staff filter, only these
  // client_ids should appear in the left sidebar (a null value means "no
  // staff filter — show every conversation").
  const [staffScopedClientIds, setStaffScopedClientIds] = useState(null);
  const [activeClientId, setActiveClientId] = useState(routeClientId || null);
  const [threadData, setThreadData] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | reply_needed
  const [productFilter, setProductFilter] = useState("all");
  const [replyText, setReplyText] = useState("");
  const [replyThreadId, setReplyThreadId] = useState("");
  const [sending, setSending] = useState(false);
  const [internalOnly, setInternalOnly] = useState(false);
  const [internalOnlyLocked, setInternalOnlyLocked] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const focusReply = () => {
    const el = textareaRef.current;
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus();
  };
  const fileInputRef = useRef(null);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [mentionState, setMentionState] = useState({ open: false, query: "", caret: 0 });
  const [handlingClientId, setHandlingClientId] = useState(null);

  const handleMarkHandled = async (clientId) => {
    setHandlingClientId(clientId);
    try {
      await markClientHandled(clientId);
      // Optimistic local update
      setConversations((prev) => prev.map((c) =>
        c.client_id === clientId
          ? { ...c, reply_needed: false, is_handled: true }
          : c
      ));
      toast.success("Marked as handled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark as handled");
    } finally {
      setHandlingClientId(null);
    }
  };

  useEffect(() => {
    getMentionableUsers().then((r) => setMentionableUsers(r.data || [])).catch(() => setMentionableUsers([]));
  }, []);

  // Keep activeClientId in sync with the URL param — critical for direct navigation
  // (e.g. from Clients page → /messages/{clientId}) and browser back/forward.
  useEffect(() => {
    if (routeClientId && routeClientId !== activeClientId) {
      setActiveClientId(routeClientId);
    }
  }, [routeClientId, activeClientId]);

  useEffect(() => {
    getMessageConversations()
      .then((r) => setConversations(r.data || []))
      .catch(() => setConversations([]))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (!activeClientId) return;
    setLoadingThread(true);
    getClientMessages(activeClientId)
      .then((r) => {
        setThreadData(r.data);
        // Default reply to the first available active project
        const firstThread = (r.data?.threads || [])[0];
        if (firstThread) setReplyThreadId(firstThread.thread_id);
      })
      .catch(() => setThreadData(null))
      .finally(() => {
        setLoadingThread(false);
        // Mark read
        markClientMessagesRead(activeClientId).catch(() => {});
        // Newest-first list → scroll to the TOP so today's message is in view.
        setTimeout(() => messagesEndRef.current?.parentElement?.scrollTo?.({ top: 0, behavior: "smooth" }), 200);
        // Optimistically clear unread count on the row
        setConversations((prev) => prev.map((c) => c.client_id === activeClientId ? { ...c, unread_count: 0 } : c));
      });
  }, [activeClientId]);

  // When a staff filter is passed in the URL (from Unified Inbox), fetch
  // the list of client_ids that person is responsible for so we can trim
  // the left-sidebar conversation list to just their queue.
  useEffect(() => {
    if (!staffFilter) {
      setStaffScopedClientIds(null);
      return;
    }
    getUnifiedInbox(staffFilter.toLowerCase())
      .then((r) => setStaffScopedClientIds(new Set((r.data?.rows || []).map((x) => x.client_id))))
      .catch(() => setStaffScopedClientIds(new Set()));
  }, [staffFilter]);

  const filteredConversations = useMemo(() => {
    return conversations.filter((c) => {
      if (filter === "reply_needed" && !c.reply_needed) return false;
      if (search && !(c.client_name || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (staffScopedClientIds && !staffScopedClientIds.has(c.client_id)) return false;
      return true;
    });
  }, [conversations, filter, search]);

  const filteredMessages = useMemo(() => {
    if (!threadData) return [];
    const base = productFilter === "all"
      ? threadData.messages
      : threadData.messages.filter((m) => m.product_type === productFilter);
    // Newest-first ordering so reps don't have to scroll past weeks of chatter
    // to see today's reply. Matches the Order Detail conversation panel. Feb 2026.
    return [...base].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [threadData, productFilter]);

  // The currently open conversation — used to know whether "Handled" state
  // is already set (so we can flip the per-message pill from actionable
  // "Mark handled" into the passive "Handled" badge).
  const activeConv = useMemo(
    () => conversations.find((c) => c.client_id === activeClientId) || null,
    [conversations, activeClientId]
  );

  // Auto-flip Internal-only when the compose text @-mentions an internal
  // teammate. Manual toggle sticks (locked) once the user clicks the chip.
  useEffect(() => {
    const tokens = (replyText.match(/@([A-Za-z][A-Za-z0-9\-_.']{0,30})/g) || [])
      .map((t) => t.slice(1).toLowerCase());
    const anyInternal = tokens.some((tok) =>
      mentionableUsers.some(
        (u) =>
          ["super_admin", "manager", "ops_coordinator"].includes(u.role) &&
          (u.name || "").toLowerCase().startsWith(tok)
      )
    );
    if (!internalOnlyLocked) setInternalOnly(anyInternal);
  }, [replyText, mentionableUsers, internalOnlyLocked]);

  const handleSend = async () => {
    if ((!replyText.trim() && pendingAttachments.length === 0) || !replyThreadId) return;
    setSending(true);
    try {
      const fileIds = pendingAttachments.map((f) => f.id);
      await postProjectComment(replyThreadId, replyText.trim(), fileIds, internalOnly);
      setReplyText("");
      setPendingAttachments([]);
      setInternalOnly(false);
      setInternalOnlyLocked(false);
      const r = await getClientMessages(activeClientId);
      setThreadData(r.data);
      setTimeout(() => messagesEndRef.current?.parentElement?.scrollTo?.({ top: 0, behavior: "smooth" }), 200);
      toast.success(internalOnly ? "Internal note posted" : "Message sent");
    } catch (err) {
      toast.error("Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB — skipped`);
          continue;
        }
        const { data } = await uploadFile(file);
        setPendingAttachments((prev) => [...prev, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => prev.filter((f) => f.id !== id));
  };

  // Detect @ trigger as user types and update mention picker state
  const onReplyChange = (e) => {
    const value = e.target.value;
    setReplyText(value);
    const caret = e.target.selectionStart || value.length;
    // Look back from caret to find the most recent @ that isn't preceded by a word char (so emails don't trigger)
    const upToCaret = value.slice(0, caret);
    const match = upToCaret.match(/(^|\s)@([A-Za-z][A-Za-z0-9\-_.']{0,30})$/);
    if (match) {
      setMentionState({ open: true, query: match[2].toLowerCase(), caret });
    } else {
      setMentionState((s) => (s.open ? { ...s, open: false } : s));
    }
  };

  const insertMention = (mUser) => {
    const value = replyText;
    const caret = mentionState.caret;
    const upToCaret = value.slice(0, caret);
    const newUpTo = upToCaret.replace(/@([A-Za-z][A-Za-z0-9\-_.']{0,30})$/, `@${(mUser.name || "").split(" ")[0]} `);
    const next = newUpTo + value.slice(caret);
    setReplyText(next);
    setMentionState({ open: false, query: "", caret: newUpTo.length });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newUpTo.length, newUpTo.length);
      }
    }, 0);
  };

  const filteredMentionables = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query;
    return mentionableUsers
      .filter((u) => (u.name || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionState, mentionableUsers]);

  const ProductChip = ({ product, onClick, active }) => {
    if (!product) return null;
    const Icon = PRODUCT_ICON[product] || MessageSquare;
    const className = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${PRODUCT_COLOR[product] || "bg-zinc-100"} ${active ? "ring-2 ring-offset-1 ring-primary" : ""}`;
    const content = (
      <>
        <Icon className="h-3 w-3" />
        {PRODUCT_LABEL[product] || product}
      </>
    );
    // When no onClick, render as a non-interactive span (avoids nested <button> when shown inside a clickable conversation row)
    if (!onClick) {
      return (
        <span className={className} data-testid={`product-chip-${product}`}>
          {content}
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        data-testid={`product-chip-${product}`}
      >
        {content}
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Messages</h1>
        <p className="text-muted-foreground mt-1">All client conversations in one place — organized by client, tagged by product.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* LEFT: Conversation list */}
        <Card className="border lg:col-span-1 flex flex-col overflow-hidden">
          <div className="p-3 border-b space-y-2 shrink-0">
            <div className="relative">
              <SearchIcon className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="messages-search"
              />
            </div>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} className="h-7 text-xs px-2">All</Button>
              <Button type="button" size="sm" variant={filter === "reply_needed" ? "default" : "outline"} onClick={() => setFilter("reply_needed")} className="h-7 text-xs px-2" data-testid="filter-reply-needed">
                Reply Needed {conversations.filter((c) => c.reply_needed).length > 0 && `(${conversations.filter((c) => c.reply_needed).length})`}
              </Button>
            </div>
            {staffFilter && (
              <div
                className="mt-1 inline-flex items-center gap-1.5 self-start rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary"
                data-testid="messages-staff-filter-pill"
              >
                <span>
                  Showing {
                    staffFilter.toLowerCase() === "mine"
                      ? ((user?.name || user?.email || "").trim().split(/[\s@]/)[0] || "your")
                      : staffFilter
                  }'s clients only
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete("staff");
                    setSearchParams(next, { replace: true });
                  }}
                  className="hover:text-primary/70"
                  data-testid="messages-staff-filter-clear"
                  title="Clear staff filter"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground px-4">
                {conversations.length === 0 ? "No client conversations yet." : "No matches."}
              </div>
            ) : (
              <div className="divide-y">
                {filteredConversations.map((c) => (
                  <div
                    key={c.client_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setActiveClientId(c.client_id); navigate(`/messages/${c.client_id}`); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { setActiveClientId(c.client_id); navigate(`/messages/${c.client_id}`); }}}
                    data-testid={`conv-row-${c.client_id}`}
                    className={`w-full text-left p-3 hover:bg-secondary/40 transition cursor-pointer ${activeClientId === c.client_id ? "bg-primary/5" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">{c.client_name || "Unknown client"}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        {c.reply_needed ? (
                          <>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleMarkHandled(c.client_id); }}
                              title="Mark this conversation as handled (no reply needed)"
                              className="inline-flex items-center justify-center h-5 px-1.5 rounded-full border border-zinc-300 hover:bg-emerald-50 hover:border-emerald-300 text-[10px] font-medium text-zinc-600 hover:text-emerald-700 transition"
                              data-testid={`mark-handled-${c.client_id}`}
                              disabled={handlingClientId === c.client_id}
                            >
                              {handlingClientId === c.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "✓ Handled"}
                            </button>
                            <span className="inline-flex items-center justify-center h-5 rounded-full bg-red-500 text-white text-[10px] font-semibold px-2" data-testid={`reply-needed-${c.client_id}`}>
                              Reply
                            </span>
                          </>
                        ) : c.is_handled && c.last_handled_by_name ? (
                          <span
                            className="inline-flex items-center gap-1 h-5 rounded-full bg-sky-100 text-sky-800 text-[10px] font-semibold px-2 border border-sky-200"
                            title={`Handled by ${c.last_handled_by_name}`}
                            data-testid={`handled-by-${c.client_id}`}
                          >
                            {getInitials(c.last_handled_by_name, c.last_handled_by_initials)} Handled
                          </span>
                        ) : INTERNAL_ROLES.has(c.last_author_role) && c.last_author_name ? (
                          <span
                            className="inline-flex items-center gap-1 h-5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 border border-emerald-200"
                            title={`Replied by ${c.last_author_name}`}
                            data-testid={`replied-by-${c.client_id}`}
                          >
                            {getInitials(c.last_author_name, c.last_author_initials)} Replied
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {(c.agency_name || c.rep_name) && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {[c.agency_name, c.rep_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      <b>{c.last_author_name || ""}</b>: {c.last_body || "(no messages)"}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(c.product_types || []).map((p) => <ProductChip key={p} product={p} />)}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.primary_project_id && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/orders/${c.primary_project_id}`); }}
                            title="Open the order for this client"
                            className="inline-flex items-center text-[10px] font-medium text-sky-700 hover:text-sky-900 hover:underline"
                            data-testid={`conv-view-order-${c.client_id}`}
                          >
                            View Order →
                          </button>
                        )}
                        <span className="text-[10px] text-muted-foreground">{timeAgo(c.last_activity)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* RIGHT: Thread */}
        <Card className="border lg:col-span-2 flex flex-col overflow-hidden">
          {!activeClientId ? (
            <div className="flex flex-1 items-center justify-center text-muted-foreground p-8">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a client conversation from the left.</p>
              </div>
            </div>
          ) : loadingThread ? (
            <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : !threadData ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Could not load this conversation.</div>
          ) : (
            <>
              {/* Header */}
              <div className="p-3 border-b shrink-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{threadData.client_name}</div>
                    {(threadData.agency_name || threadData.rep_name) && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {[threadData.agency_name, threadData.rep_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {threadData.primary_project_id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/orders/${threadData.primary_project_id}`)}
                        className="h-7 text-xs"
                        data-testid="active-conv-view-order"
                        title="Open this client's order in a full view"
                      >
                        <ExternalLink className="mr-1 h-3 w-3" /> View Order
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => setProductFilter("all")}
                      className={`text-xs px-2 py-0.5 rounded-full border ${productFilter === "all" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                      data-testid="product-filter-all"
                    >All</button>
                    {(threadData.active_products || []).map((p) => (
                      <ProductChip key={p} product={p} onClick={() => setProductFilter(p)} active={productFilter === p} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Reply composer */}
              <div className="border-b p-3 shrink-0 bg-white relative" data-testid="reply-composer-top">
                {/* @mention picker dropdown */}
                {mentionState.open && filteredMentionables.length > 0 && (
                  <div
                    className="absolute top-full left-3 mt-1 w-64 max-h-56 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg z-50"
                    data-testid="mention-picker"
                  >
                    {filteredMentionables.map((mu) => (
                      <button
                        type="button"
                        key={mu.id}
                        onMouseDown={(e) => { e.preventDefault(); insertMention(mu); }}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2 text-sm"
                        data-testid={`mention-option-${mu.id}`}
                      >
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
                          {getInitials(mu.name, mu.display_initials)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{mu.name}</div>
                          <div className="text-[10px] text-muted-foreground">{mu.role?.replace("_", " ")}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {/* Pending attachments strip — shown before send */}
                {pendingAttachments.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5" data-testid="mp-pending-attachments">
                    {pendingAttachments.map((f) => (
                      <span key={f.id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary text-[11px] font-medium border" data-testid={`mp-pending-attachment-${f.id}`}>
                        <FileText className="h-3 w-3" />
                        <span className="truncate max-w-[180px]" title={f.filename}>{f.filename}</span>
                        <button type="button" onClick={() => removePendingAttachment(f.id)} className="text-muted-foreground hover:text-destructive" title="Remove attachment">
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Internal-only toggle — same behavior as the order-view
                    conversation compose. Auto-flips on with @internal-mention. */}
                <div className="mb-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setInternalOnly(!internalOnly); setInternalOnlyLocked(true); }}
                    data-testid="mp-internal-toggle"
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                      internalOnly
                        ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200"
                        : "bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100"
                    }`}
                    title={internalOnly
                      ? "This message will be visible to internal staff only — the rep will not see it."
                      : "Post as a public message. Click to make it internal-only."}
                  >
                    <Lock className="h-3 w-3" />
                    {internalOnly ? "Internal only" : "Post publicly"}
                  </button>
                  {internalOnly && (
                    <span className="text-[10px] text-amber-700">
                      Rep + client will NOT see this comment
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleAttach}
                    data-testid="mp-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    title="Attach file(s) — 25 MB max each"
                    data-testid="mp-attach-btn"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </Button>
                  <RichTextarea
                    ref={textareaRef}
                    value={replyText}
                    onChange={onReplyChange}
                    placeholder="Type your reply... (use @name to ping a teammate)"
                    rows={3}
                    wrapperClassName="flex-1 min-w-0"
                    className="w-full min-h-[84px] resize-y"
                    data-testid="reply-textarea"
                    onKeyDown={(e) => {
                      if (e.key === "Escape" && mentionState.open) {
                        setMentionState((s) => ({ ...s, open: false }));
                        return;
                      }
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <div className="flex sm:flex-col gap-1.5 shrink-0 w-full sm:w-auto justify-end">
                    <Select value={replyThreadId} onValueChange={setReplyThreadId}>
                      <SelectTrigger className="h-9 text-xs w-[140px]" data-testid="reply-thread-select">
                        <SelectValue placeholder="Tag to..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(threadData.threads || []).map((t) => (
                          <SelectItem key={t.thread_id} value={t.thread_id} className="text-xs">
                            {t.product_type ? PRODUCT_LABEL[t.product_type] : t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleSend}
                      disabled={sending || (!replyText.trim() && pendingAttachments.length === 0) || !replyThreadId}
                      size="sm"
                      data-testid="reply-send-btn"
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Send className="h-3.5 w-3.5 mr-1" /> Send</>)}
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Tip: Ctrl/⌘+Enter to send · @name to ping a teammate (emails them) · 📎 to attach a report</p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/20">
                {filteredMessages.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-10">
                    No messages yet. Start one above.
                  </div>
                ) : filteredMessages.map((m, idx) => {
                  const mine = m.author_id === user?.id;
                  const authorIsInternal = INTERNAL_ROLES.has(m.author_role);
                  const isExternal = !mine && !authorIsInternal;
                  const viewerIsInternal = INTERNAL_ROLES.has(user?.role);
                  // Show a Handled pill on external (rep/client) messages only,
                  // and only for internal viewers. Ticking any external message
                  // dismisses the whole conversation from "Reply Needed" —
                  // reused from the existing markClientHandled endpoint.
                  const showHandledPill = isExternal && viewerIsInternal;
                  const alreadyHandled = !!activeConv?.is_handled;
                  return (
                    <div key={idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-lg px-3 py-2 border ${mine ? "bg-primary text-primary-foreground border-primary/30" : "bg-white"} ${m.internal_only ? "ring-2 ring-amber-400 border-amber-400" : ""}`}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-[11px] font-medium ${mine ? "text-primary-foreground/80" : "text-foreground"}`}>{m.author_name || "User"}</span>
                          {m.internal_only && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase bg-amber-500 text-white rounded-full px-1.5 py-0.5"
                              title="Internal only — visible to super admins, managers, and ops staff. Reps and clients will not see it."
                              data-testid={`mp-msg-internal-badge-${idx}`}
                            >
                              <Lock className="h-2.5 w-2.5" /> Internal
                            </span>
                          )}
                          {m.product_type && (
                            <Badge variant="outline" className={`${PRODUCT_COLOR[m.product_type]} text-[10px] py-0 px-1.5 h-4`}>
                              {PRODUCT_LABEL[m.product_type]}
                            </Badge>
                          )}
                          <span className={`text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</span>
                  {!mine && (
                    <button type="button" onClick={focusReply}
                      className="text-[10px] font-semibold text-primary hover:underline inline-flex items-center gap-0.5"
                      data-testid={`msg-reply-btn-${idx}`}>
                      <CornerUpLeft className="h-3 w-3" /> Reply
                    </button>
                  )}
                          {showHandledPill && (
                            alreadyHandled ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5"
                                data-testid={`msg-handled-badge-${idx}`}
                                title="This thread is already marked handled — a new reply from the rep will bring it back to Reply Needed."
                              >
                                <Check className="h-3 w-3" /> Handled
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleMarkHandled(activeClientId); }}
                                disabled={handlingClientId === activeClientId}
                                data-testid={`msg-handled-btn-${idx}`}
                                title="Mark this thread handled — clears it from Reply Needed until the next external reply."
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 rounded-full px-1.5 py-0.5 transition disabled:opacity-60"
                              >
                                <CheckCircle2 className="h-3 w-3" /> Handled?
                              </button>
                            )
                          )}
                        </div>
                        <div className="text-sm whitespace-pre-wrap leading-snug">{renderMentionedBody(m.body)}</div>
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div className={`mt-2 pt-2 border-t space-y-1 ${mine ? "border-primary-foreground/25" : "border-zinc-200"}`}>
                            {m.attachments.map((att) => (
                              <a
                                key={att.id}
                                href={`${API_BASE}/api/files/${att.id}/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-1.5 text-[11px] font-medium truncate hover:underline ${mine ? "text-primary-foreground" : "text-primary"}`}
                                data-testid={`msg-attachment-${att.id}`}
                                title={`Download ${att.filename}`}
                              >
                                <Paperclip className="h-3 w-3 shrink-0" />
                                <span className="truncate">{att.filename}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

            </>
          )}
        </Card>
      </div>
    </div>
  );
}
